import { verticalListSortingStrategy, type SortingStrategy } from "@dnd-kit/sortable";
import { reorderIndices } from "../../util/reorder-lines";

const NO_SHIFT = { x: 0, y: 0, scaleX: 1, scaleY: 1 };

/**
 * Sorting strategy for dragging several rows at once: the rows left behind close up the space the
 * whole block vacated and open a block-sized gap at the drop target — what verticalListSortingStrategy
 * does, but for more than one row.
 *
 * Rows are uniform height, so a row's transform is simply the offset between the slot it sits in now
 * and the slot it would land in. The landing order comes from reorderIndices — the same helper that
 * performs the drop — so the preview can't disagree with where the rows actually end up.
 */
export const createMultiDragSortingStrategy = (draggedIndices: Array<number>): SortingStrategy => {
  if (draggedIndices.length <= 1) return verticalListSortingStrategy;

  const draggedIndexSet = new Set(draggedIndices);

  return ({ rects, activeIndex, overIndex, index }) => {
    // The dragged rows follow the pointer instead of the shuffle, so the strategy leaves them alone.
    if (draggedIndexSet.has(index)) return NO_SHIFT;

    const order = reorderIndices(rects.length, activeIndex, overIndex, draggedIndices);
    const newIndex = order?.indexOf(index) ?? -1;
    if (newIndex === -1) return null;
    if (newIndex === index) return NO_SHIFT;

    return { x: 0, y: rects[newIndex].top - rects[index].top, scaleX: 1, scaleY: 1 };
  };
};
